import { useEffect, useState } from 'react';
import { Loader2, MailCheck, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { MfaSetupCard } from '@/components/security/MfaSetupCard';
import {
  changeAdminPassword, adminRunShareReminders, adminGetShareReminderSettings, adminUpdateShareReminderSettings,
} from '@/lib/api';
import { isPasswordStrongEnough } from '@/lib/passwordStrength';
import { ADMIN_DASHBOARD, SHARE_REMINDERS } from '@/constants/testIds';
import { GearProfileCard } from '@/components/admin/GearProfileCard';

const LEAD_DAY_OPTIONS = [1, 2, 3, 5, 7, 10, 14];

export const AdminSettingsTab = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [reminderRunning, setReminderRunning] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [leadDays, setLeadDays] = useState(null); // saved value from server
  const [leadDraft, setLeadDraft] = useState('3');
  const [leadSaving, setLeadSaving] = useState(false);

  useEffect(() => {
    adminGetShareReminderSettings()
      .then((s) => { setLeadDays(s.lead_days); setLeadDraft(String(s.lead_days)); })
      .catch(() => setLeadDays(3));
  }, []);

  const handleSaveLeadDays = async () => {
    setLeadSaving(true);
    try {
      const s = await adminUpdateShareReminderSettings(Number(leadDraft));
      setLeadDays(s.lead_days);
      toast.success(`Clients will now be reminded ${s.lead_days} day${s.lead_days === 1 ? '' : 's'} before their link expires.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the reminder lead time.');
    } finally {
      setLeadSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrongEnough(newPassword)) {
      toast.error('Please meet all the password requirements below.');
      return;
    }
    setLoading(true);
    try {
      await changeAdminPassword(currentPassword, newPassword);
      toast.success('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  const handleRunReminders = async () => {
    setReminderRunning(true);
    try {
      const result = await adminRunShareReminders();
      setReminderResult(result);
      if (result.reminded.length > 0) {
        toast.success(`Sent ${result.reminded.length} expiry reminder${result.reminded.length === 1 ? '' : 's'}.`);
      } else {
        toast.success('No gallery links are close enough to expiry yet.');
      }
    } catch {
      toast.error('Could not run the expiry reminder check.');
    } finally {
      setReminderRunning(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="max-w-md rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8">
        <h3 className="font-display text-xl text-[hsl(var(--lxra-fg))] mb-1">Change Password</h3>
        <p className="text-sm text-[hsl(var(--lxra-muted))] mb-6">Update your studio admin login password.</p>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid={ADMIN_DASHBOARD.changePasswordForm}>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Current Password</Label>
            <Input
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">New Password</Label>
            <Input
              type="password"
              required
              minLength={10}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]"
            />
            <PasswordStrengthMeter password={newPassword} testIdPrefix={ADMIN_DASHBOARD.changePasswordForm} />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
          </Button>
        </form>
      </div>

      <div className="max-w-md rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8" data-testid={SHARE_REMINDERS.card}>
        <h3 className="font-display text-xl text-[hsl(var(--lxra-fg))] mb-1">Gallery Share Reminders</h3>
        <p className="text-sm text-[hsl(var(--lxra-muted))] mb-6">
          Clients are automatically emailed before their gallery share link expires (checked hourly).
          Choose how much notice they get, or run the check right now.
        </p>

        <div className="mb-6 space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Remind clients</Label>
          <div className="flex items-center gap-2">
            <Select value={leadDraft} onValueChange={setLeadDraft} disabled={leadDays === null}>
              <SelectTrigger
                data-testid={SHARE_REMINDERS.leadDaysSelect}
                className="h-10 w-52 bg-[#141416] border-[hsl(var(--lxra-border))] text-sm text-[hsl(var(--lxra-fg))]"
              >
                <Clock className="h-3.5 w-3.5 mr-2 text-[hsl(var(--lxra-gold))]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                {LEAD_DAY_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>{d} day{d === 1 ? '' : 's'} before</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleSaveLeadDays}
              disabled={leadSaving || leadDays === null || String(leadDays) === leadDraft}
              data-testid={SHARE_REMINDERS.leadDaysSave}
              className="h-10 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-50 text-xs uppercase tracking-[0.1em] font-semibold"
            >
              {leadSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
          {leadDays !== null && (
            <p data-testid={SHARE_REMINDERS.leadDaysLabel} className="text-[11px] text-[hsl(var(--lxra-muted))]">
              Currently reminding {leadDays} day{leadDays === 1 ? '' : 's'} before expiry.
            </p>
          )}
        </div>

        <Button
          onClick={handleRunReminders}
          disabled={reminderRunning}
          data-testid={SHARE_REMINDERS.runButton}
          variant="outline"
          className="h-10 px-5 rounded-lg border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10 disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
        >
          {reminderRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MailCheck className="h-4 w-4 mr-2" />}
          Check Now
        </Button>
        {reminderResult && (
          <p className="mt-4 text-xs text-[hsl(var(--lxra-muted))]" data-testid={SHARE_REMINDERS.resultText}>
            {reminderResult.reminded.length > 0
              ? `Reminded: ${reminderResult.reminded.map((r) => r.client_email).join(', ')}`
              : 'No galleries currently need a reminder.'}
          </p>
        )}
      </div>

      <GearProfileCard />

      <MfaSetupCard />
    </div>
  );
};
